import {type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {useBoardFocusGrid, useBoardFocusStore} from '../focus/contexts'
import {cellPosition, isSamePosition} from '../focus/positions'
import {
  BoardCanMoveCardsContext,
  BoardDragPointerYContext,
  BoardDropPlacementContext,
  type BoardInteraction,
  BoardInteractionContext,
  BoardLandingContext,
  NO_DRAGGING_CARDS,
  useBoardElementRef,
  useBoardItemDropPolicy,
} from '../provider/contexts'
import type {BoardCardAddress, BoardItemsMove} from '../types'
import {
  type Announcements,
  type CollisionDescriptor,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  MouseSensor,
  pointerWithin,
  type ScreenReaderInstructions,
  TouchSensor,
  useSensor,
  useSensors,
} from './dnd-kit'
import {type BoardPendingLanding, createBoardLandingStore, hasLanded, isFocusNowhere, stopWaitingAfter} from './landing'
import type {BoardDropPlacement} from './placement'
import {resolveBoardDrop} from './resolve-drop'
import {readBoardCardDragData, readBoardCellDropData} from './types'

/*
  A drag says nothing, and this is what makes that true rather than an accident.

  dnd-kit announces by default, and its wording interpolates the drag node id: dragging
  a card announces "Draggable item 4:todo:3:all:t1 was dropped.", which is the opaque
  composed key `provider/cell-key.ts` builds, in hard-coded English, on a board where
  every other string comes from the caller's `labels`. It announces from a monitor on the
  context rather than from a handler, so it speaks for every drag inside it -- including
  one `onDragStart` below read and declined, which is a drag the board has nothing to say
  about at all.

  Naming a card is what `getItemLabel` is for, and building an announcement out of it is
  https://github.com/github/views/issues/48, together with the keyboard drag there is
  wording to describe.

  So this is silence held on purpose until that lands, not a default nobody chose.
  `undefined` rather than an empty string, because dnd-kit skips a nullish announcement
  and puts anything else in the live region.
*/
const NOTHING_ANNOUNCED: Announcements = {
  onDragStart: () => undefined,
  onDragMove: () => undefined,
  onDragOver: () => undefined,
  onDragEnd: () => undefined,
  onDragCancel: () => undefined,
}

/*
  The same silence, for the description every draggable points at. dnd-kit's default
  reads "To pick up a draggable item, press the space bar. While dragging, use the arrow
  keys to move the item", which describes the keyboard drag the sensors below deliberately
  leave out. Offering it would put an instruction for a gesture that does nothing in front
  of exactly the reader least able to discover that.

  Empty rather than absent: `draggable` is a required string and dnd-kit draws the element
  either way, so this makes it an element with nothing in it. An `aria-describedby`
  pointing at one contributes no description, which is the state to be in until
  https://github.com/github/views/issues/48 supplies a real one.
*/
const NO_DRAG_INSTRUCTIONS: ScreenReaderInstructions = {draggable: ''}

/**
 * Holds the board's drag, publishes what it is carrying, and reports what a drop made of
 * it.
 *
 * Nothing registers as a drag source here: a card registers its own node in
 * `cells/use-board-card.ts`, and every cell registers itself as a drop target in
 * `cells/use-board-cell.ts`. What this layer settles is where the context lives and what
 * it says, so both arrive as hook calls rather than as a new prop at any call site.
 *
 * **A drop is reported, and the board moves no card.** It writes nothing and holds no move
 * of its own: it calls `onItemsMove` and carries on drawing the data it was given, so the
 * card is where it was until the caller hands back data saying otherwise. Once that data
 * shows the move, the card that arrived is marked as landed, which `drag/landing.ts`
 * describes. The board waits for that while a promise `onItemsMove` returned is pending,
 * and for `LANDING_WAIT_MS` after, so a move the caller refuses never lands on a later,
 * unrelated change.
 *
 * **Supplying `onItemsMove` is what makes any of it happen.** Without it there is nobody to
 * tell that a card was dropped, so the board offers no drag at all: no card registers as a
 * drag source, no cell registers as a drop target, and the two hooks answer as they do on a
 * board at rest. Gating only the drop would leave the gesture answering every way it can --
 * the card lifts, a copy follows the pointer, the cells scroll under it -- before
 * discarding the drop, which is a worse gesture than none. `provider/contexts.ts` carries
 * the fact to the card and the cell, and `getItemDragState` is the separate, narrower
 * question of which cards may move on a board that moves them.
 *
 * dnd-kit arrives through `drag/dnd-kit.ts`, which is this package's one door to the
 * library. Moving to a shared multi-dimensional wrapper later is a change to that file
 * and to nothing else.
 *
 * **This context covers everything a caller renders inside `BoardProvider`, which is
 * wider than the board.** A draggable binds to the nearest context above it, so a control
 * a caller renders beside the board body -- the composition `@github-ui/board` documents
 * -- drags on the board's terms rather than its own. `onDragStart` declining a payload it
 * cannot read keeps the board quiet about such a drag; it does not hand the drag back to
 * whoever owns it.
 *
 * Narrowing it means splitting where the state lives from where the context is mounted,
 * because the report below has to stay readable above the board's own markup. That is
 * https://github.com/github/views/issues/162, and it is worth noting the narrower shape
 * trades this mistake for the opposite one: a caller who leaves the boundary out gets a
 * board that never drags. Nothing composes a board that way today.
 *
 * **`children` is a prop so that a drag starting does not redraw the board.** Everything
 * below is built by `BoardProvider`, so the element is the same one across this
 * component's own state changes and React bails out of the subtree; only the cards
 * reading the interaction context wake up.
 *
 * That keeps the board's interaction state off the cards. It does not keep dnd-kit's off
 * them, and nothing here can: every card reads dnd-kit's own `InternalContext`, which is
 * memoised on the activator event, on `active`, on the active card's measured rect and on
 * `over`. The activator event goes in on `mousedown` and comes out on `mouseup`, so **a
 * press draws every card in the window again twice, whether or not it becomes a drag**,
 * and a drag that does start adds a few more as those other three settle. After that only
 * `over` changes, so a card re-renders per cell crossed rather than per pointer move, and
 * a draggable that is not the active one reads a null transform context and does not
 * re-render on movement at all. Virtualization bounds all of it to the drawn window. It is
 * the same cost the two dnd-kit boards already in this repository pay.
 */
export function BoardDragProvider({
  onItemsMove,
  children,
}: {
  onItemsMove?: (move: BoardItemsMove) => void | Promise<unknown>
  children: ReactNode
}): ReactNode {
  const [draggingCards, setDraggingCards] = useState<readonly BoardCardAddress[]>(NO_DRAGGING_CARDS)

  /*
    The caller's own callback, held rather than closed over, so `onDragEnd` below stays one
    function for the life of the board. Following `reporting/BoardReportingProvider.tsx`,
    which holds its own for the same two reasons: a caller building the handler inline
    would otherwise hand `DndContext` a new prop on every render of the page around the
    board, and a caller that swapped the handler in the same render that ended a drag would
    be called through the one it had just replaced.

    `useEffectEvent` reads the current one too and would be the obvious choice, but it
    hands back a new function on every render, so anything memoised on it is memoised on
    nothing.
  */
  const onItemsMoveRef = useRef(onItemsMove)

  useLayoutEffect(() => {
    onItemsMoveRef.current = onItemsMove
  }, [onItemsMove])

  const grid = useBoardFocusGrid()
  const {dispatch, getState} = useBoardFocusStore()
  const boardElementRef = useBoardElementRef()
  const [landing] = useState(createBoardLandingStore)
  const pendingLandingRef = useRef<BoardPendingLanding | null>(null)

  /*
    Marks the moved card once the caller's data shows it, and puts focus back on it.

    Focus goes back only when the card had focus at the drop and focus is now nowhere in
    particular. Removing the focused card fires `release` before this runs, so the
    reducer's own `hasFocus` no longer says the reader was there. A drop is a pointer
    gesture, so focus comes back without the keyboard ring.
  */
  useEffect(() => {
    const pending = pendingLandingRef.current
    if (pending === null || !hasLanded(grid.itemIdsAt, pending)) return

    pendingLandingRef.current = null
    landing.land(pending.card)

    const active = (boardElementRef.current?.ownerDocument ?? document).activeElement
    if (pending.hadFocus && isFocusNowhere(active, boardElementRef.current)) {
      dispatch({type: 'focus', grid, position: cellPosition(pending.card, pending.card.itemId), byPointer: true})
    }
  }, [grid, landing, dispatch, boardElementRef])

  /*
    Where in the cell under the pointer the held card would land, written by that cell and
    read here when the drop happens.

    The cell owns the answer because it is the only thing that knows both halves of it: its
    own loaded ids, and which of its cards are drawn to measure the pointer against. This
    is the seam that carries it to the one handler that has to report it, without the board
    re-rendering on every pointer move.

    Cleared when a drag begins as well as when one ends, so a drop that lands on nothing --
    outside every cell, or on a droppable of the caller's own -- can never be answered by
    the last placement some earlier drag left behind.
  */
  const dropPlacementRef = useRef<BoardDropPlacement | null>(null)

  /*
    How far down the viewport the pointer is, kept for the cell it is over to measure a
    place inside itself against.

    A cell binds its own listener while it is the one under the pointer, and so hears every
    move made in it; the move that carried the drag in is the one it cannot hear, because
    it happened before that listener existed. Read from here on the way in, a reader who
    crosses into a cell and holds still is answered from where they actually are, rather
    than being told nothing until they move again -- which is exactly the moment the cell
    scrolls under them or a page arrives beneath them.

    One listener for the board rather than one per drawn cell, bound only while the board
    is carrying a card of its own. A ref, so none of it re-renders anything.

    Bound from a layout effect rather than a passive one, because a cell reads this from a
    passive effect of its own. React may leave passive effects until after the paint, so a
    listener bound in one would miss every move made in the meantime -- and a cell seeding
    itself afterwards would read a position older than the move that carried the drag in.
    At commit, this is bound before any of that, so whatever arrives is recorded first and
    the cell reads the latest of it.
  */
  const dragPointerYRef = useRef<number | null>(null)
  const isDraggingCard = draggingCards.length > 0

  useLayoutEffect(() => {
    if (!isDraggingCard) return

    const track = (event: PointerEvent) => {
      dragPointerYRef.current = event.clientY
    }

    window.addEventListener('pointermove', track)

    return () => {
      window.removeEventListener('pointermove', track)
      dragPointerYRef.current = null
    }
  }, [isDraggingCard])

  /*
    Pointer input only, and not because the keyboard does not matter.

    dnd-kit's defaults are `PointerSensor` and `KeyboardSensor`, and the keyboard one
    activates on `Space` and `Enter` -- which is the card's own selection key. Both
    handlers would be an `onKeyDown` on the same card element, so the layer above would
    have to merge them, and merging settles nothing: dnd-kit refuses to activate on an
    event whose default was already prevented, so selection first means `Space` never
    picks a card up, and the sensor first means
    `selection/use-board-card-selection.ts` sees the prevented event and `Space` stops
    selecting, which is shipped behaviour regressing.

    `Space` cannot mean both, and which one it should mean is not this layer's to settle.
    Keyboard-driven moves are https://github.com/github/views/issues/49, and the
    `aria-roledescription` and `aria-describedby` that have to announce them are
    https://github.com/github/views/issues/48. They arrive together, which is what makes
    leaving them out here safe: there is no keyboard drag going unannounced.

    The activation distance matters on its own. Neither `PointerSensor` nor `MouseSensor`
    has a threshold by default, so a click that moves a pixel reads as a drag and the
    card's click-to-select becomes unreliable.
  */
  const sensors = useSensors(
    useSensor(MouseSensor, {activationConstraint: {distance: 4}}),
    useSensor(TouchSensor, {activationConstraint: {delay: 250, tolerance: 5}}),
  )

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const drag = readBoardCardDragData(event.active)
      // Something else in the caller's tree is being dragged. Not the board's to report.
      if (!drag) return

      dropPlacementRef.current = null
      pendingLandingRef.current = null
      landing.clear()

      const {columnKey, rowKey, itemId} = drag
      setDraggingCards([{columnKey, rowKey, itemId}])
    },
    [landing],
  )

  // Read from drag start rather than from `active`: dnd-kit reports `{}` as the data of a
  // picked-up card once it unmounts, which windowing does when the board scrolls it away.
  const heldCard = draggingCards[0]

  // The shared constant rather than a fresh `[]`, so the context value is one object for
  // the whole time nobody is dragging and a board at rest re-renders nothing.
  const onDragCancel = useCallback(() => {
    dropPlacementRef.current = null
    setDraggingCards(NO_DRAGGING_CARDS)
  }, [])

  /*
    A separate handler from the cancel above, and that is the whole of what keeps `Escape`
    from moving a card. dnd-kit populates `over` on a cancelled drag exactly as it does on
    a completed one, so one handler doing both would resolve a move every time somebody
    abandoned a drag over a cell.

    Nothing is reported for a drag this package cannot read, for a drop on anything that is
    not one of its cells, and for a drop that leaves the card where it already was.
    `resolve-drop.ts` holds all three answers, and the placement the cell under the pointer
    published is what tells the last of them apart from a real reordering.
  */
  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggingCards(NO_DRAGGING_CARDS)

      const move = resolveBoardDrop(heldCard, readBoardCellDropData(event.over), dropPlacementRef.current)

      dropPlacementRef.current = null

      const report = onItemsMoveRef.current
      if (move === null || heldCard === undefined || report === undefined) return

      const {from, to, anchor} = move
      const {position, hasFocus} = getState()
      const pending: BoardPendingLanding = {
        card: {columnKey: to.columnKey, rowKey: to.rowKey, itemId: heldCard.itemId},
        from,
        anchor,
        // `hasFocus` as well as the position, because the position names a card before any
        // card has focus, and a touch press focuses nothing.
        hadFocus: hasFocus && isSamePosition(position, cellPosition(heldCard, heldCard.itemId)),
      }
      pendingLandingRef.current = pending

      const stop = () => {
        if (pendingLandingRef.current === pending) pendingLandingRef.current = null
      }

      let reply: unknown
      try {
        reply = report(move)
      } catch (error) {
        stop()
        throw error
      }

      // Left unhandled on purpose, so a refusal still reaches whatever reports the caller's errors.
      void stopWaitingAfter(reply, stop)
    },
    [heldCard, getState],
  )

  const interaction = useMemo<BoardInteraction>(() => ({draggingCards}), [draggingCards])

  /*
    `pointerWithin`, less every cell the caller refuses the held card. A refused cell is
    never `over`, so it is not drawn as a target, publishes no placement and reports no move.
    Only the cells under the pointer are asked, so the cost does not grow with the board.

    Not `useDroppable({disabled})` per cell: dnd-kit applies `disabled` from a passive
    effect, which lags behind the start of a drag and changes the enabled cells mid-drag.
  */
  const dropPolicy = useBoardItemDropPolicy()
  const collisionDetection = useCallback<CollisionDetection>(
    args => {
      const collisions = pointerWithin(args)
      if (!heldCard) return collisions

      return collisions.filter(({data}) => {
        const drop = readBoardCellDropData((data as CollisionDescriptor['data'] | undefined)?.droppableContainer)

        return drop === null || dropPolicy(heldCard, {columnKey: drop.columnKey, rowKey: drop.rowKey})
      })
    },
    [dropPolicy, heldCard],
  )

  /*
    The prop's presence rather than the ref above, which holds the same handler for a
    different reason: the ref exists so a drop reaches whichever handler the caller has by
    then, and render may not read one anyway.
  */
  const canMoveCards = onItemsMove !== undefined

  /*
    `DndContext` draws two visually hidden elements of its own, a live region and the
    description a draggable points at. `BoardProvider` renders no DOM, and both packages
    hold a caller to that: `@github-ui/board` asserts that a board puts exactly one
    element in the box around it, which is what a caller's own CSS selects on. So the two
    go to the end of the document instead. The description still resolves, since
    `aria-describedby` is by id and reads a hidden element.

    **The live region does not, inside a modal.** A `<dialog>` opened modally makes the
    rest of the document inert, and an inert subtree is out of the accessibility tree, so
    a region parked on the body announces nothing from a board inside one. Nothing is
    announced at all today, for the reason recorded above the announcements, so this
    settles nothing that https://github.com/github/views/issues/48 cannot revisit, and the
    answer there is probably a container taken from the board's own root.

    Read during render rather than in an effect. The document's body is the same element
    for the life of the page, so there is nothing here to go stale and nothing to react
    to, and setting it from an effect would cost the board a second render on mount for a
    value that was already known. Nothing is drawn before it: `DndContext` draws neither
    element until an effect of its own has run.
  */
  const accessibility = useMemo(
    () => ({
      announcements: NOTHING_ANNOUNCED,
      screenReaderInstructions: NO_DRAG_INSTRUCTIONS,
      // eslint-disable-next-line @github-ui/github-monorepo/no-stale-read-in-render
      container: typeof document === 'undefined' ? undefined : document.body,
    }),
    [],
  )

  return (
    /*
      `autoScroll={false}` is not optional. The board scrolls its own two containers with
      an axis lock and per-axis buffers, and two things scrolling one container fight each
      other. `contract.server.test.ts` fails any `DndContext` in this package that omits it.

      `collisionDetection` is set for the same kind of reason: the default is wrong for
      this board rather than merely different. dnd-kit's `rectIntersection` picks the
      target overlapping the dragged card's *translated* rect most, and that rect is the
      card's own, moved by the sensor's delta. On this board the card deliberately does
      not move -- a copy of it follows the pointer instead, for the reason recorded in
      `BoardDragOverlay` -- so a rect nobody can see would be choosing the destination
      while a reader aimed with the copy, and a card grabbed by one corner would aim from
      somewhere the pointer never was.

      `pointerWithin` is the strategy that matches what a reader is holding. It also
      answers with nothing at all when the pointer is between two cells or over a header,
      where an area strategy would still name whichever neighbour it overlapped most; on
      a board where every cell is a target and a drop is a move, naming a cell the pointer
      is not in is the more expensive mistake. Cells tile the band and nothing smaller
      registers, so wherever the pointer is over a cell at all there is exactly one
      answer.

      It takes the pointer from the sensor's own coordinates, which both sensors here
      supply. A keyboard sensor supplies none and would collide with nothing, so
      https://github.com/github/views/issues/49 has a strategy to revisit when it adds one.
      `collisionDetection` above wraps it to apply the caller's `canDropItem`.
    */
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      autoScroll={false}
      accessibility={accessibility}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <BoardCanMoveCardsContext value={canMoveCards}>
        <BoardDropPlacementContext value={dropPlacementRef}>
          <BoardDragPointerYContext value={dragPointerYRef}>
            <BoardLandingContext value={landing}>
              <BoardInteractionContext value={interaction}>{children}</BoardInteractionContext>
            </BoardLandingContext>
          </BoardDragPointerYContext>
        </BoardDropPlacementContext>
      </BoardCanMoveCardsContext>
    </DndContext>
  )
}
